import { useState, type FormEvent } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/da242rrk8r5te5mqsg1g';

type State = 'idle' | 'success' | 'error';

export default function NewsletterSection() {
  const [state, setState] = useState<State>('idle');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // 反垃圾：蜜罐字段被填则静默当作成功处理
    const honeypot = (fd.get('contact_alt') || '').toString().trim();
    if (honeypot) {
      form.reset();
      setState('success');
      return;
    }
    fd.delete('contact_alt');

    setSubmitting(true);
    setFormError('');
    setState('idle');
    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';

      if (res.ok && parsed?.code === 'OK') {
        form.reset();
        setState('success');
      } else {
        setFormError(serverMsg && !serverMsg.includes('spam') ? serverMsg : '订阅失败，请稍后再试');
        setState('error');
      }
    } catch {
      setFormError('网络异常，请稍后再试');
      setState('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="subscribe" className="py-16 md:py-20 bg-background-100">
      <div className="max-w-5xl mx-auto px-4 md:px-6">
        <div className="relative overflow-hidden rounded-lg bg-primary-500 px-6 py-10 md:px-12 md:py-14 text-center">
          <i className="ri-hearts-fill absolute -top-6 -right-4 text-primary-300/40 text-[120px] rotate-12"></i>
          <i className="ri-heart-3-fill absolute -bottom-8 -left-6 text-primary-200/30 text-[100px]"></i>

          <div className="relative z-10">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-white bg-white/15 rounded-full">
              <i className="ri-mail-send-line"></i>
              订阅更新
            </span>
            <h2 className="mt-4 font-heading text-2xl md:text-4xl text-white">不错过每一部心动甜番</h2>
            <p className="mt-3 max-w-xl mx-auto text-sm md:text-base text-white/85">
              每周精选新番 + 心动榜单第一时间送到邮箱，订阅即开启追番提醒，随时挑一部开刷。
            </p>

            <form
              id="subscription-form"
              data-readdy-form="subscription-form"
              onSubmit={handleSubmit}
              className="mt-7 max-w-xl mx-auto"
              noValidate={false}
            >
              <div className="flex flex-col sm:flex-row items-stretch gap-2 sm:gap-3">
                <label className="sr-only" htmlFor="sub-email">邮箱地址</label>
                <input
                  id="sub-email"
                  name="email"
                  type="email"
                  required
                  placeholder="请输入邮箱地址"
                  autoComplete="email"
                  className="w-full flex-1 px-4 py-3 text-sm text-foreground-900 bg-white rounded-md placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-medium text-primary-700 bg-white hover:bg-background-50 rounded-md whitespace-nowrap transition-colors disabled:opacity-70"
                >
                  {submitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      提交中
                    </>
                  ) : (
                    <>
                      <i className="ri-heart-3-line"></i>
                      订阅甜番
                    </>
                  )}
                </button>
              </div>

              <div className="field-wrap-offscreen" aria-hidden="true">
                <label htmlFor="sub-contact-alt">补充信息</label>
                <input
                  id="sub-contact-alt"
                  name="contact_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                />
              </div>
            </form>

            {state === 'success' && (
              <p className="mt-4 text-sm text-white bg-white/15 inline-flex items-center gap-1.5 px-4 py-2 rounded-full">
                <i className="ri-checkbox-circle-fill"></i>
                订阅成功，心动甜番马上送到你邮箱～
              </p>
            )}
            {state === 'error' && (
              <p className="mt-4 text-sm text-white bg-white/15 inline-flex items-center gap-1.5 px-4 py-2 rounded-full">
                <i className="ri-close-circle-fill"></i>
                {formError}
              </p>
            )}

            <p className="mt-5 text-xs text-white/70">我们仅用于发送更新通知，绝不骚扰、绝不出售你的信息。</p>
          </div>
        </div>
      </div>
    </section>
  );
}